import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useT } from "../../context/LanguageContext";
import { toast } from "sonner";
import {
  ChevronLeft, Lock, CheckCircle2, Clock, AlertTriangle, MessageCircle,
  CalendarClock, Loader2, ChevronRight,
} from "lucide-react";

/**
 * V2 / Mobile-native H2H room.
 * Renders inside <MobileShell> at /v2/h2h/:opponentId so players never break
 * out to the V1 desktop UI when they tap "H2H" from a player profile.
 *
 * Reuses unchanged backend endpoints:
 *   GET  /h2h/:opponentId                       → both legs + opponent + league
 *   POST /matches/:id/reschedule                → propose date
 *   DELETE /matches/:id/reschedule              → withdraw proposal
 *
 * Result submission is delegated to the existing /v2/matches/:id page so we
 * don't duplicate the screenshot-upload form here.
 */
export default function H2HV2() {
  const { opponentId } = useParams();
  const { user } = useAuth();
  const { t } = useT();
  const nav = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const prevStatusRef = useRef({});

  const refresh = async (silent = false) => {
    try {
      const { data: fresh } = await api.get(`/h2h/${opponentId}`);

      // Detect status transitions on each leg → toast.
      const prev = prevStatusRef.current;
      const next = {};
      for (const m of fresh.matches || []) {
        next[m.id] = m.status;
        const before = prev[m.id];
        if (before && before !== m.status) {
          if (m.status === "confirmed") {
            const winId = m.confirmed_score_home > m.confirmed_score_away
              ? m.home_id
              : m.confirmed_score_away > m.confirmed_score_home
                ? m.away_id
                : null;
            const youWon = winId && winId === user?.id;
            const opponentWon = winId && winId !== user?.id;
            if (youWon) {
              toast.success(t("v2.h2h.toast.win", {
                round: m.round, h: m.confirmed_score_home, a: m.confirmed_score_away,
              }));
            } else if (opponentWon) {
              toast(t("v2.h2h.toast.loss", {
                round: m.round, h: m.confirmed_score_home, a: m.confirmed_score_away,
              }));
            } else {
              toast(t("v2.h2h.toast.draw", {
                round: m.round, h: m.confirmed_score_home, a: m.confirmed_score_away,
              }));
            }
          } else if (m.status === "disputed") {
            toast.error(t("v2.h2h.toast.disputed", { round: m.round }));
          }
        }
      }
      prevStatusRef.current = next;
      setData(fresh);
    } catch (e) {
      if (!silent) setErr(formatApiError(e));
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => {
    refresh();
    // Poll for opponent submissions every 5s while on this page.
    const iv = setInterval(() => refresh(true), 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [opponentId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-[#52525B]" data-testid="v2-h2h-loading">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="px-5 pt-5">
        <BackBar onBack={() => nav(-1)} />
        <div className="mt-4 rounded-2xl bg-[#E11D48]/10 border border-[#E11D48]/30 p-4 text-sm text-[#FCA5A5]" data-testid="v2-h2h-error">
          {err}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const opponent = data.opponent || {};
  const me = data.me || {};
  const matches = [...(data.matches || [])].sort((a, b) => (a.order || 1) - (b.order || 1));
  const game1 = matches.find((x) => (x.order || 1) === 1);
  const waNumber = opponent?.whatsapp ? opponent.whatsapp.replace(/[^0-9]/g, "") : null;

  return (
    <div className="px-5 pt-5 pb-6 space-y-4" data-testid="v2-h2h-page">
      <BackBar onBack={() => nav(-1)} />

      {/* Header */}
      <section className="rounded-3xl bg-gradient-to-br from-[#1F1F2A] via-[#12121C] to-[#0A0A14] border border-[#27272F] p-5 shadow-2xl shadow-black/40" data-testid="v2-h2h-header">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#A5B4FC]">
          {t("v2.h2h.kicker")} {data.league?.name && <span className="text-[#71717A]">· {data.league.name}</span>}
        </div>
        <div className="mt-1 text-xl font-bold text-white truncate">
          {me?.username} <span className="text-[#71717A] text-base mx-1">vs</span> {opponent?.username}
        </div>
        <p className="mt-2 text-[11px] text-[#A1A1AA] leading-relaxed">{t("v2.h2h.help")}</p>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Link
            to={`/v2/chat/${opponent.id}`}
            data-testid="v2-h2h-message-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#7C3AED]/15 border border-[#7C3AED]/40 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-[#A5B4FC] hover:bg-[#7C3AED]/25 transition-colors"
          >
            <MessageCircle className="w-3 h-3" /> {t("v2.h2h.message")}
          </Link>
          {waNumber && (
            <a
              href={`https://wa.me/${waNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="v2-h2h-whatsapp"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-[#25D366] hover:bg-[#25D366]/25 transition-colors"
            >
              WA
            </a>
          )}
          <Link
            to={`/v2/players/${encodeURIComponent(opponent.username || "")}`}
            data-testid="v2-h2h-profile-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#12121C] border border-[#1F1F2A] px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-[#A1A1AA] hover:border-[#7C3AED] hover:text-white transition-colors"
          >
            {t("v2.h2h.profile")}
          </Link>
        </div>
      </section>

      {/* Legs */}
      <section className="space-y-3" data-testid="v2-h2h-legs">
        {matches.map((m) => (
          <LegCard
            key={m.id}
            match={m}
            me={user}
            game1={game1}
            onChanged={refresh}
            t={t}
          />
        ))}
      </section>
    </div>
  );
}

function BackBar({ onBack }) {
  const { t } = useT();
  return (
    <button
      type="button"
      onClick={onBack}
      data-testid="v2-h2h-back"
      className="inline-flex items-center gap-1.5 text-[#A1A1AA] hover:text-white text-xs font-mono uppercase tracking-[0.25em] transition-colors"
    >
      <ChevronLeft className="w-4 h-4" />
      {t("v2.match.back")}
    </button>
  );
}

function LegCard({ match, me, game1, onChanged, t }) {
  const isHome = me?.id === match.home_id;
  const isAway = me?.id === match.away_id;
  const finalized = match.status === "confirmed" || match.admin_override;
  const isGame2 = match.order === 2;
  const game1Finalized = !game1 || game1.status === "confirmed" || game1.admin_override;
  const locked = isGame2 && !game1Finalized;

  const mySubmission = isHome ? match.home_submission : isAway ? match.away_submission : null;
  const oppSubmission = isHome ? match.away_submission : isAway ? match.home_submission : null;

  const myUsername = isHome ? match.home?.username : match.away?.username;
  const oppUsername = isHome ? match.away?.username : match.home?.username;

  const homeScore = match.confirmed_score_home;
  const awayScore = match.confirmed_score_away;
  const myScore = isHome ? homeScore : awayScore;
  const oppScore = isHome ? awayScore : homeScore;

  return (
    <div
      className="rounded-2xl bg-[#12121C] border border-[#1F1F2A] p-4 space-y-3"
      data-testid={`v2-h2h-leg-${match.order || 1}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest ${
            (match.order || 1) === 1
              ? "bg-[#7C3AED]/20 text-[#A5B4FC] border border-[#7C3AED]/40"
              : "bg-[#EA580C]/20 text-[#FDBA74] border border-[#EA580C]/40"
          }`}>
            {t("v2.h2h.game", { n: match.order || 1 })}
          </span>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">
            {isHome ? t("v2.match.home") : isAway ? t("v2.match.away") : "—"}
          </span>
        </div>
        <LegStatus status={match.status} adminOverride={match.admin_override} locked={locked} t={t} />
      </div>

      {/* Score */}
      <div className="grid grid-cols-3 items-center gap-2">
        <div className="text-right truncate">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#10B981]">{t("v2.match.you")}</div>
          <div className="text-xs text-white truncate">{myUsername || "—"}</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold text-white tabular-nums">
            {myScore ?? "·"}
            <span className="mx-2 text-[#52525B]">–</span>
            {oppScore ?? "·"}
          </div>
        </div>
        <div className="truncate">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[#A5B4FC]">{t("v2.h2h.opp")}</div>
          <div className="text-xs text-white truncate">{oppUsername || "—"}</div>
        </div>
      </div>

      {/* Submission badges */}
      <div className="grid grid-cols-2 gap-2">
        <SubBadge label={t("v2.h2h.yourSubmission")} present={!!mySubmission} t={t} />
        <SubBadge label={t("v2.h2h.oppSubmission")} present={!!oppSubmission} t={t} />
      </div>

      {/* CTA */}
      {!locked && (
        <Link
          to={`/v2/matches/${match.id}`}
          data-testid={`v2-h2h-leg-cta-${match.order || 1}`}
          className="w-full inline-flex items-center justify-between gap-2 rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white font-bold text-sm py-3 px-4 shadow-lg shadow-[#7C3AED]/30 hover:opacity-95 transition-opacity"
        >
          <span>
            {finalized
              ? t("v2.h2h.cta.view")
              : mySubmission
                ? t("v2.h2h.cta.waiting")
                : t("v2.h2h.cta.submit")}
          </span>
          <ChevronRight className="w-4 h-4" />
        </Link>
      )}
      {locked && (
        <div className="rounded-2xl bg-[#0F0F18] border border-dashed border-[#1F1F2A] py-3 px-4 inline-flex items-center justify-center gap-2 text-[#71717A] text-xs font-mono uppercase tracking-widest" data-testid={`v2-h2h-leg-locked-${match.order || 1}`}>
          <Lock className="w-3 h-3" /> {t("v2.h2h.locked")}
        </div>
      )}

      {/* Reschedule */}
      {!finalized && !locked && (
        <ReschedulePanel match={match} me={me} onChanged={onChanged} t={t} />
      )}
    </div>
  );
}

function SubBadge({ label, present, t }) {
  const accent = present ? "#10B981" : "#52525B";
  return (
    <div
      className="rounded-xl bg-[#0F0F18] border border-[#1F1F2A] px-3 py-2"
      style={{ borderColor: present ? `${accent}55` : "#1F1F2A" }}
    >
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#71717A]">{label}</div>
      <div className="mt-0.5 text-[11px] font-bold uppercase tracking-widest" style={{ color: accent }}>
        {present ? t("v2.match.submission.received") : t("v2.match.submission.waiting")}
      </div>
    </div>
  );
}

function LegStatus({ status, adminOverride, locked, t }) {
  if (locked) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-[#52525B]/20 text-[#A1A1AA] border border-[#52525B]/40"
        data-testid="v2-h2h-leg-status"
      >
        <Lock className="w-2.5 h-2.5" /> {t("v2.h2h.locked")}
      </span>
    );
  }
  if (adminOverride) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-[#7C3AED]/15 text-[#A5B4FC] border border-[#7C3AED]/40"
        data-testid="v2-h2h-leg-status"
      >
        {t("v2.match.status.admin")}
      </span>
    );
  }
  const map = {
    confirmed: { Icon: CheckCircle2, color: "#10B981", label: t("v2.match.status.confirmed") },
    pending:   { Icon: Clock,        color: "#F59E0B", label: t("v2.match.status.pending") },
    disputed:  { Icon: AlertTriangle,color: "#E11D48", label: t("v2.match.status.disputed") },
    scheduled: { Icon: Clock,        color: "#A1A1AA", label: t("v2.match.status.scheduled") },
  };
  const cfg = map[status] || map.scheduled;
  const Icon = cfg.Icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest"
      style={{ backgroundColor: `${cfg.color}22`, color: cfg.color, border: `1px solid ${cfg.color}55` }}
      data-testid="v2-h2h-leg-status"
    >
      <Icon className="w-2.5 h-2.5" />
      {cfg.label}
    </span>
  );
}

function ReschedulePanel({ match, me, onChanged, t }) {
  const proposals = match.reschedule_proposals || {};
  const myProposal = me?.id ? proposals[me.id] : null;
  const otherEntries = Object.entries(proposals).filter(([uid]) => uid !== me?.id);
  const otherProposal = otherEntries.length ? otherEntries[0][1] : null;

  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(myProposal || match.scheduled_at || "");
  const [busy, setBusy] = useState(false);

  const today = new Date().toISOString().split("T")[0];

  const submit = async () => {
    if (!date || date < today) {
      toast.error(t("v2.h2h.reschedule.errFuture"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/${match.id}/reschedule`, { date });
      if (data.applied) toast.success(t("v2.h2h.reschedule.applied", { date }));
      else toast(t("v2.h2h.reschedule.proposed", { date }));
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      await api.delete(`/matches/${match.id}/reschedule`);
      toast.success(t("v2.h2h.reschedule.withdrawn"));
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const acceptOpponent = async () => {
    if (!otherProposal) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/matches/${match.id}/reschedule`, { date: otherProposal });
      if (data.applied) toast.success(t("v2.h2h.reschedule.applied", { date: otherProposal }));
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-3 border-t border-[#1F1F2A]" data-testid={`v2-h2h-reschedule-${match.id}`}>
      {otherProposal && otherProposal !== myProposal && (
        <div className="mb-3 rounded-xl bg-[#7C3AED]/10 border border-[#7C3AED]/40 px-3 py-2 flex items-center justify-between gap-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#A5B4FC] truncate">
            {t("v2.h2h.reschedule.oppProposed", { date: otherProposal })}
          </div>
          <button
            type="button"
            onClick={acceptOpponent}
            disabled={busy}
            data-testid={`v2-h2h-reschedule-accept-${match.id}`}
            className="flex-shrink-0 rounded-full bg-[#7C3AED] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 hover:bg-[#6D28D9] transition-colors disabled:opacity-50"
          >
            {t("v2.h2h.reschedule.accept")}
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">
          {t("v2.h2h.reschedule.scheduled")}: <span className="text-white">{match.scheduled_at || "—"}</span>
        </div>
        <div className="flex items-center gap-2">
          {myProposal && (
            <button
              type="button"
              onClick={withdraw}
              disabled={busy}
              data-testid={`v2-h2h-reschedule-withdraw-${match.id}`}
              className="rounded-full border border-[#1F1F2A] text-[#A1A1AA] hover:border-[#E11D48] hover:text-[#E11D48] text-[10px] font-bold uppercase tracking-widest px-3 py-1 transition-colors disabled:opacity-50"
            >
              {t("v2.h2h.reschedule.withdraw")}
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid={`v2-h2h-reschedule-toggle-${match.id}`}
            className="inline-flex items-center gap-1 rounded-full border border-[#7C3AED]/40 text-[#A5B4FC] hover:bg-[#7C3AED]/15 text-[10px] font-bold uppercase tracking-widest px-3 py-1 transition-colors"
          >
            <CalendarClock className="w-3 h-3" /> {t("v2.h2h.reschedule.cta")}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 flex gap-2 items-center" data-testid={`v2-h2h-reschedule-form-${match.id}`}>
          <input
            type="date"
            min={today}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid={`v2-h2h-reschedule-date-${match.id}`}
            className="flex-1 rounded-xl bg-[#0F0F18] border border-[#1F1F2A] focus:border-[#7C3AED] focus:outline-none px-3 py-2 text-xs text-white transition-colors"
          />
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid={`v2-h2h-reschedule-submit-${match.id}`}
            className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white text-[10px] font-bold uppercase tracking-widest px-4 py-2 hover:opacity-95 transition-opacity disabled:opacity-50"
          >
            {t("v2.h2h.reschedule.send")}
          </button>
        </div>
      )}

      {myProposal && otherProposal !== myProposal && (
        <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#71717A]">
          {t("v2.h2h.reschedule.youProposed", { date: myProposal })}
        </div>
      )}
    </div>
  );
}
